import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Loader2, Save } from "lucide-react";

export const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const INIT = { base: "prospect", prospect_id: "", entreprise_id: "", nb_salaries: 1, heures_semaine: 21, duree_mois: 3,
  salaire_horaire: 15, contrat: "cdi", secteur: "administratif", frais_directs: 0, tva: "8.5", poste: "" };

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

function Choice({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function DevisForm({ canEdit, onSaved }) {
  const [f, setF] = useState(INIT);
  const [opts, setOpts] = useState({ prospects: [], entreprises: [] });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    Promise.all([api.get("/prospects").catch(() => ({ data: { items: [] } })), api.get("/entreprises").catch(() => ({ data: [] }))])
      .then(([p, e]) => setOpts({ prospects: p.data.items || [], entreprises: e.data || [] }));
  }, []);
  const set = (k) => (v) => { setRes(null); setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v })); };
  const body = () => {
    const num = ["nb_salaries", "heures_semaine", "duree_mois", "salaire_horaire", "frais_directs"];
    const b = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "").map(([k, v]) => [k, num.includes(k) ? Number(v) : v]));
    if (b.base === "membre") { delete b.tva; delete b.prospect_id; } else delete b.entreprise_id;
    return b;
  };
  const run = async (save) => {
    setBusy(save ? "save" : "sim");
    try {
      const { data } = await api.post(save ? "/devis" : "/devis/simuler", body());
      setRes(data);
      if (save) { toast.success(`Devis ${data.numero} enregistré (PDF en GED)`); onSaved(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };
  const r = res?.resultat;
  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6" data-testid="devis-form">
      <div className="grid sm:grid-cols-2 gap-3 rounded-xl border border-slate-200 p-5 bg-white">
        <Field label="Base de calcul"><Choice value={f.base} onChange={set("base")} testid="devis-base"
          options={[["prospect", "Prospect — moteur du simulateur public"], ["membre", "Entreprise membre — paramètres GE"]]} /></Field>
        {f.base === "prospect"
          ? <Field label="Demande du simulateur (facultatif)"><Choice value={f.prospect_id} onChange={set("prospect_id")} testid="devis-prospect" placeholder="Prospect libre"
              options={opts.prospects.map((p) => [p.id, `${p.organisation} — ${p.email}`])} /></Field>
          : <Field label="Entreprise membre"><Choice value={f.entreprise_id} onChange={set("entreprise_id")} testid="devis-entreprise" placeholder="Choisir…"
              options={opts.entreprises.map((e) => [e.id, e.raison_sociale])} /></Field>}
        <Field label="Poste"><Input value={f.poste} onChange={set("poste")} data-testid="devis-poste" /></Field>
        <Field label="Nombre de salariés"><Input type="number" min="1" value={f.nb_salaries} onChange={set("nb_salaries")} data-testid="devis-nb" /></Field>
        <Field label="Heures / semaine / salarié"><Input type="number" value={f.heures_semaine} onChange={set("heures_semaine")} data-testid="devis-heures" /></Field>
        <Field label="Durée (mois)"><Input type="number" value={f.duree_mois} onChange={set("duree_mois")} data-testid="devis-duree" /></Field>
        <Field label="Salaire brut horaire (€)"><Input type="number" step="0.01" value={f.salaire_horaire} onChange={set("salaire_horaire")} data-testid="devis-salaire" /></Field>
        <Field label="Contrat"><Choice value={f.contrat} onChange={set("contrat")} testid="devis-contrat" options={[["cdi", "CDI"], ["cdd", "CDD (+10 % fin de contrat)"]]} /></Field>
        <Field label="Frais directs de mission (€)"><Input type="number" value={f.frais_directs} onChange={set("frais_directs")} data-testid="devis-frais" /></Field>
        {f.base === "prospect" && <Field label="TVA"><Choice value={f.tva} onChange={set("tva")} testid="devis-tva"
          options={[["8.5", "8,5 % (DOM)"], ["20", "20 %"], ["0", "0 %"], ["pending", "À déterminer"]]} /></Field>}
        <div className="sm:col-span-2 flex gap-2 pt-2">
          <Button variant="outline" onClick={() => run(false)} disabled={!!busy} data-testid="devis-simuler">
            {busy === "sim" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Calculator className="h-4 w-4 mr-1" />}Simuler</Button>
          {canEdit && <Button onClick={() => run(true)} disabled={!!busy || !r} data-testid="devis-enregistrer" className="bg-[#17352e] text-white hover:bg-[#284b40]">
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Enregistrer le devis</Button>}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 p-5 bg-[#FDF9F1]" data-testid="devis-resultat">
        {!r ? <p className="text-sm text-slate-500">Renseignez les hypothèses puis « Simuler ».</p> : (<>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{res.destinataire?.nom} · {r.moteur}</div>
          <table className="w-full text-sm"><tbody>
            {r.lignes.map(([l, m]) => <tr key={l} className="border-b border-slate-200/70"><td className="py-1.5">{l}</td><td className="text-right tabular-nums">{eur(m)}</td></tr>)}
            <tr className="font-semibold"><td className="py-2">Total HT</td><td className="text-right" data-testid="devis-total-ht">{eur(r.sous_total_ht)}</td></tr>
            <tr><td>TVA</td><td className="text-right">{r.tva == null ? "À déterminer" : eur(r.tva)}</td></tr>
            {r.ftpe > 0 && <tr><td>FTPE (TTC)</td><td className="text-right">{eur(r.ftpe)}</td></tr>}
            <tr className="font-semibold text-[#17352e]"><td className="py-2">Total TTC</td><td className="text-right" data-testid="devis-total-ttc">{r.total_ttc == null ? "—" : eur(r.total_ttc)}</td></tr>
          </tbody></table>
          <div className="text-xs text-slate-500 mt-2">{Number(r.heures_totales).toFixed(1)} h · {eur(r.cout_horaire_mission)} / h</div>
          <ul className="mt-3 space-y-1 text-[11px] text-slate-500 list-disc pl-4">{r.avertissements.map((w) => <li key={w}>{w}</li>)}</ul>
        </>)}
      </div>
    </div>
  );
}
